import React, { useState } from "react";
import { FiltersButton, InfoBanner, ScrollToTopBtn, Spinner, TableHeader, WithRoleAccess } from "@services/ui-components";
import { Button, Col, Row } from "react-bootstrap";
import { ErrorModalComponent } from "@me-pos/error-modal";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { ApolloError, useReactiveVar } from "@apollo/client";
import { AddProductToTechMapFilter } from "../types";
import { useGetOneTechMapQuery } from "@me-team/host/main/technologicalMaps/graphql/technologicalMaps.hooks";
import ErrorService from "../../../services/ErrorService";
import AddProductToTechMapFilterWrapper from "./AddProductToTechMapFilterWrapper";
import AddProductToTechMapTable from "./AddProductToTechMapTable";
import UpdateRecoveryTechMap from "../../TechnologicalMaps/UpdateRecoveryTechMap/UpdateRecoveryTechMap";
import { ActionUpdateType } from "../../../utils/enums";
import { CompanyAccess, TechnologicalMapProxy } from "@me-team/host/main/graphql/types";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";

const AddProductToTechMap: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const { techMapId, branchId } = useParams();
    const [showFilters, setShowFilters] = useState(false);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number>(null);
    const [showUpdateRecoveryTechMapModal, setShowUpdateRecoveryTechMapModal] = useState(false);

    const {
        data: oneTechMapData,
        loading: oneTechMapLoading,
        refetch: oneTechMapRefetch,
    } = useGetOneTechMapQuery({
        variables: {
            techMapId: +techMapId,
            companyId: +currentCompanyId,
        },
        skip: !currentCompanyId,
        context: {
            errorType: "global",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });
    const techMap: TechnologicalMapProxy = oneTechMapData?.user?.company?.technologicalMaps?.technologicalMaps?.[0];
    const techMapName: string = techMap?.name;

    const initialValueFilter: AddProductToTechMapFilter = {
        nameSkuBarcode: null,
        categoryId: null,
        brand: null,
    };

    const [filters, setFilters] = useState<AddProductToTechMapFilter>(initialValueFilter);

    const toggleFilters = () => setShowFilters(!showFilters);
    const toggleUpdateTechMapModal = () => setShowUpdateRecoveryTechMapModal((prevState) => !prevState);

    if (oneTechMapLoading || !currentCompanyId) return <Spinner />;

    return (
        <div className="mb-9 px-2 px-lg-0">
            <InfoBanner>
                <p className="fs-7 mb-0">
                    {t(
                        "Click the “+” next to the product to add it to the technological card. To delete an item, click “-”."
                    )}
                </p>
            </InfoBanner>
            <TableHeader
                title={`${t("Add New Product to")} ${techMapName}`}
                paths={[
                    {
                        path: `/warehouse/technological-maps/${currentCompanyId}/${techMapId}/product/add/branch/${branchId}`,
                        label: t("Warehouse accounting"),
                    },
                    {
                        path: `/warehouse/technological-maps/${currentCompanyId}/branch/${branchId}`,
                        label: t("List of Technological Maps"),
                    },
                    {
                        path: `/warehouse/technological-maps/${currentCompanyId}/${techMapId}/products/branch/${branchId}`,
                        label: `${t("Products")} ${techMapName}`,
                    },
                    {
                        path: `/warehouse/technological-maps/${currentCompanyId}/${techMapId}/product/add/branch/${branchId}`,
                        label: `${t("Add New Product to")} ${techMapName}`,
                    },
                ]}
                colSize={7}
                btnColSize={5}
                buttons={
                    <Row className="g-3 justify-content-end align-items-center mt-4 mt-lg-0">
                        <WithRoleAccess permission={CompanyAccess.TechMapProductListView}>
                            <Col sm md={6} lg={"auto"} xl={"auto"}>
                                <Button
                                    variant="outline-primary"
                                    className="w-100"
                                    onClick={() => {
                                        navigate(
                                            `/warehouse/technological-maps/${currentCompanyId}/${techMapId}/products/branch/${branchId}`
                                        );
                                    }}
                                >
                                    {t("Back to tech map products")}
                                </Button>
                            </Col>
                        </WithRoleAccess>
                        <Col sm={"auto"} md={6} lg={2} xl={4}>
                            <FiltersButton
                                filter={filters}
                                showFilters={showFilters}
                                onClick={toggleFilters}
                                classes="px-4 px-md-1"
                                classesText="d-lg-none d-xl-inline-block"
                            />
                        </Col>
                    </Row>
                }
                showFilters={showFilters}
            >
                <AddProductToTechMapFilterWrapper setFilters={setFilters} />
            </TableHeader>
            <AddProductToTechMapTable filters={filters} />
            <ScrollToTopBtn />
            <UpdateRecoveryTechMap
                show={showUpdateRecoveryTechMapModal}
                onClose={toggleUpdateTechMapModal}
                currentAction={ActionUpdateType.Edit}
                currentUpdateTechMap={techMap}
                refetchTechMaps={oneTechMapRefetch}
            />

            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
        </div>
    );
};

export default AddProductToTechMap;
